// apps/web/src/pages/settings/operations.ts — GraphQL operation constants for the settings surface.
//
// Per plan 02-04 Task 3: the operation strings live here (not in
// lib/api.ts) so each settings page imports just the documents it
// needs. The types mirror the API contract in apps/api/src/graphql/schema.ts.
//
// The `gql` tag from graphql-request is used so the .request() call
// in lib/api.ts receives a DocumentNode (with operation name) and the
// 401-retry interceptor can detect "this is a settings op" by name.

import { gql } from "graphql-request";

export const RESTAURANT_QUERY = gql`
  query RestaurantSettings {
    restaurant {
      id
      name
      slug
      timezone
      slotDurationMinutes
      capacityDefault
      holdbackDefault
      addressLine
      postalCode
      city
      phone
      email
      contactNotes
      confirmationMode
      minPartySize
      maxPartySize
      maxAdvanceDays
      minAdvanceMinutes
      sameDayCutoffTime
      tagline {
        de
        en
      }
      heroCopy {
        de
        en
      }
      aboutText {
        de
        en
      }
    }
  }
`;

export const OPENING_HOURS_QUERY = gql`
  query OpeningHoursSettings {
    openingHours {
      id
      weekday
      openTime
      closeTime
      closeNextDay
    }
  }
`;

export const DAY_EXCEPTIONS_QUERY = gql`
  query DayExceptionsSettings {
    dayExceptions {
      id
      date
      status
      openTime
      closeTime
      closeNextDay
    }
  }
`;

export const AREAS_QUERY = gql`
  query AreasSettings {
    areas {
      id
      key
      enabled
      label {
        de
        en
      }
    }
  }
`;

export const UPDATE_RESTAURANT_MUTATION = gql`
  mutation UpdateRestaurantSettings($input: UpdateRestaurantInput!) {
    updateRestaurant(input: $input) {
      id
      name
      slotDurationMinutes
      capacityDefault
      holdbackDefault
    }
  }
`;

export const UPSERT_OPENING_HOURS_MUTATION = gql`
  mutation UpsertOpeningHoursSettings($rows: [OpeningHourInput!]!) {
    upsertOpeningHours(rows: $rows) {
      weekday
      openTime
      closeTime
      closeNextDay
    }
  }
`;

export const UPSERT_DAY_EXCEPTION_MUTATION = gql`
  mutation UpsertDayExceptionSettings($input: DayExceptionInput!) {
    upsertDayException(input: $input) {
      id
      date
      status
      openTime
      closeTime
      closeNextDay
    }
  }
`;

// 260723-pqo: idempotent DayException removal. Returns true even when
// the row no longer exists so the UI can re-invalidate and refetch
// safely; the server's `payload.deleted` field carries the distinction
// in the AuditLog for forensic queries.
export const DELETE_DAY_EXCEPTION_MUTATION = gql`
  mutation DeleteDayExceptionSettings($id: ID!) {
    deleteDayException(id: $id)
  }
`;

export const TOGGLE_AREA_MUTATION = gql`
  mutation ToggleAreaSettings($key: AreaKey!, $enabled: Boolean!) {
    toggleArea(key: $key, enabled: $enabled) {
      key
      enabled
    }
  }
`;

// ---- Phase 03 plan 03-03: admin availability surface ----

const SLOT_DEFINITION_FIELDS = `
  id
  startTime
  durationMinutes
  active
  capacityOverride
  holdbackOverride
  weekdayBinding
`;

const CAPACITY_CONFIG_FIELDS = `
  id
  date
  slotDefinitionId
  onlineHoldback
  telefonHoldback
  vorOrtHoldback
  capacityOverride
  slotCapacityOverride
`;

const BLOCKED_PERIOD_FIELDS = `
  id
  fromDate
  toDate
  fromTime
  toTime
  reason
`;

export const SLOT_DEFINITIONS_QUERY = gql`
  query SlotDefinitionsSettings {
    slotDefinitions {
      ${SLOT_DEFINITION_FIELDS}
    }
  }
`;

export const CAPACITY_CONFIGS_QUERY = gql`
  query CapacityConfigsSettings {
    capacityConfigs {
      ${CAPACITY_CONFIG_FIELDS}
    }
  }
`;

export const BLOCKED_PERIODS_QUERY = gql`
  query BlockedPeriodsSettings {
    blockedPeriods {
      ${BLOCKED_PERIOD_FIELDS}
    }
  }
`;

export const CREATE_SLOT_DEFINITION_MUTATION = gql`
  mutation CreateSlotDefinitionSettings($input: SlotDefinitionInput!) {
    createSlotDefinition(input: $input) {
      ${SLOT_DEFINITION_FIELDS}
    }
  }
`;

export const UPDATE_SLOT_DEFINITION_MUTATION = gql`
  mutation UpdateSlotDefinitionSettings(
    $id: ID!
    $input: SlotDefinitionInput!
  ) {
    updateSlotDefinition(id: $id, input: $input) {
      ${SLOT_DEFINITION_FIELDS}
    }
  }
`;

export const DELETE_SLOT_DEFINITION_MUTATION = gql`
  mutation DeleteSlotDefinitionSettings($id: ID!) {
    deleteSlotDefinition(id: $id)
  }
`;

export const UPSERT_CAPACITY_CONFIG_MUTATION = gql`
  mutation UpsertCapacityConfigSettings($input: CapacityConfigInput!) {
    upsertCapacityConfig(input: $input) {
      ${CAPACITY_CONFIG_FIELDS}
    }
  }
`;

export const CREATE_BLOCKED_PERIOD_MUTATION = gql`
  mutation CreateBlockedPeriodSettings(
    $input: BlockedPeriodInput!
    $forceFlag: Boolean!
  ) {
    createBlockedPeriod(input: $input, forceFlag: $forceFlag) {
      blockedPeriod {
        ${BLOCKED_PERIOD_FIELDS}
      }
      conflicts {
        id
        date
        time
        partySize
      }
      conflictingReservationCount
      requiresForce
    }
  }
`;

export const DELETE_BLOCKED_PERIOD_MUTATION = gql`
  mutation DeleteBlockedPeriodSettings($id: ID!) {
    deleteBlockedPeriod(id: $id)
  }
`;

export const UPDATE_CONFIRMATION_MODE_MUTATION = gql`
  mutation UpdateConfirmationModeSettings($mode: ConfirmationMode!) {
    updateConfirmationMode(mode: $mode)
  }
`;
